/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useMemo, useRef, useState } from "react";

import { Eye, FileText, FileType, FileUp, Upload } from "lucide-react";

import { FieldStatusIcon } from "../shared/FieldStatusIcon";

import type { ExperimentFormData } from "../../types/experiment.types";

interface SupportingDocsSectionProps {
  experimentData: ExperimentFormData;
  setExperimentData: (
    updater: (prev: ExperimentFormData) => ExperimentFormData,
  ) => void;
}

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXTENSIONS = [".pdf", ".ppt", ".pptx", ".doc", ".docx"];

export const SupportingDocsSection: React.FC<SupportingDocsSectionProps> = ({
  experimentData,
  setExperimentData,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const fileSummary = useMemo(() => {
    const pendingItems =
      experimentData.supportingDocFiles?.map((file) => ({
        name: file.name,
        size: file.size,
        type: file.type,
      })) ?? [];
    const uploadedDocs =
      experimentData.supportingDocs?.map((doc) => ({
        name: doc.filename ?? doc.name ?? "attachment",
        url: doc.url ?? "",
        format: doc.format ?? "unknown",
      })) ?? [];
    return { pendingItems, uploadedDocs };
  }, [experimentData.supportingDocFiles, experimentData.supportingDocs]);

  const formatBytes = (bytes: number): string => {
    if (!bytes || bytes <= 0) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    const idx = Math.min(
      units.length - 1,
      Math.floor(Math.log(bytes) / Math.log(1024)),
    );
    const value = bytes / 1024 ** idx;
    return `${value.toFixed(value >= 10 || idx === 0 ? 0 : 1)} ${units[idx]}`;
  };

  const getFileExt = (name: string): string => {
    const lower = name.toLowerCase();
    const lastDot = lower.lastIndexOf(".");
    if (lastDot === -1) return "file";
    const ext = lower.slice(lastDot + 1).trim();
    return ext || "file";
  };

  const splitFiles = (selected: File[]) => {
    const valid: File[] = [];
    const rejected: File[] = [];

    selected.forEach((file) => {
      const lowerName = file.name.toLowerCase();
      const isAllowed = ALLOWED_EXTENSIONS.some((ext) =>
        lowerName.endsWith(ext),
      );
      const isSizeOk = file.size <= MAX_FILE_SIZE_BYTES;

      if (isAllowed && isSizeOk) {
        valid.push(file);
      } else {
        rejected.push(file);
      }
    });

    return { valid, rejected };
  };

  const onFilesSelected = (selected: FileList | null) => {
    if (!selected) return;
    const arr = Array.from(selected);
    const { valid, rejected } = splitFiles(arr);

    if (rejected.length > 0) {
      setUploadError(
        "Some files were skipped. Only pdf, pptx, docx up to 10MB are supported.",
      );
    } else {
      setUploadError(null);
    }

    if (valid.length === 0) return;

    setExperimentData((prev) => ({
      ...prev,
      supportingDocFiles: [...(prev.supportingDocFiles ?? []), ...valid],
    }));
  };

  return (
    <div className="rounded-[12xl] p-6">
      <div className="flex items-center gap-3">
        <FieldStatusIcon
          status={
            (experimentData.supportingDocFiles?.length ?? 0) > 0 ||
            (experimentData.supportingDocs?.length ?? 0) > 0
              ? "complete"
              : "empty"
          }
        />
        <h3 className="text-xl font-semibold text-white">
          Supporting documentation{" "}
          <span className="text-white/40">(optional)</span>
        </h3>
      </div>

      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          onFilesSelected(e.dataTransfer.files);
        }}
        className="mt-4 cursor-pointer rounded-md border border-dashed border-white/20 bg-black/40 p-10 text-center hover:border-white/30"
      >
        <Upload className="mx-auto mb-3 h-6 w-6 text-blue-400" />
        <div className="text-lg font-semibold text-white">
          Drag & drop files here or click to browse
        </div>
        <div className="mt-1 text-white/60">
          Supports pdf, pptx, docx (max 10mb)
        </div>
        <button
          type="button"
          className="mt-5 rounded-md border border-amber-500/50 bg-transparent px-6 py-2 text-amber-300 hover:bg-amber-400/10"
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
        >
          Choose files
        </button>

        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          accept=".pdf,.ppt,.pptx,.doc,.docx"
          onChange={(e) => onFilesSelected(e.target.files)}
        />
      </div>

      {uploadError && (
        <div className="mt-3 text-xs text-red-300">{uploadError}</div>
      )}

      {(fileSummary.pendingItems.length > 0 ||
        fileSummary.uploadedDocs.length > 0) && (
        <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.03] p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-white">
              <FileUp size={16} className="text-amber-300" />
              Attached files
            </div>
            <div className="text-xs text-white/50">
              {fileSummary.pendingItems.length + fileSummary.uploadedDocs.length}{" "}
              item(s)
            </div>
          </div>

          <div className="space-y-2">
            {fileSummary.pendingItems.map((item, idx) => {
              const ext = getFileExt(item.name);
              return (
                <div
                  key={`pending-${item.name}-${idx}`}
                  className="flex items-center justify-between rounded-md border border-white/10 bg-black/40 px-3 py-2"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-amber-400/10 text-amber-300">
                      <FileText size={16} />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm text-white">
                        {item.name}
                      </div>
                      <div className="text-xs text-white/50">
                        {formatBytes(item.size)} · {ext.toUpperCase()}
                      </div>
                    </div>
                  </div>
                  <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 text-[10px] font-semibold text-amber-300">
                    Pending
                  </span>
                </div>
              );
            })}

            {fileSummary.uploadedDocs.map((doc, idx) => {
              const label = doc.name;
              return (
                <div
                  key={`uploaded-${label}-${idx}`}
                  className="flex items-center justify-between rounded-md border border-white/10 bg-black/40 px-3 py-2"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-emerald-400/10 text-emerald-300">
                      <FileType size={16} />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm text-white">{label}</div>
                      <div className="text-xs text-white/50">
                        {(doc.format || getFileExt(label)).toUpperCase()} ·
                        Uploaded
                      </div>
                    </div>
                  </div>
                  {doc.url ? (
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-md border border-emerald-400/30 bg-emerald-400/10 px-2 py-1 text-[10px] font-semibold text-emerald-200 hover:bg-emerald-400/20"
                    >
                      <Eye size={12} />
                      Open
                    </a>
                  ) : (
                    <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-200">
                      Ready
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p className="mt-4 text-sm text-white/50">
        Upload content that helps FalconAI generate better insights: PRD,
        business cases, user research, past test results, customer feedback or
        product overview
      </p>
    </div>
  );
};
